<template>
  <modal-wrapper
    ref="modalRef"
    class="csv-parsing-modal"
    :display-footer="false"
    :header="$t('csvParsingErrorsModal.csvErrors')"
    :size="ModalSize.L"
  >
    <table>
      <thead>
        <tr>
          <th>
            {{ $t('csvParsingErrorsModal.error') }}
          </th>
          <th>
            {{ $t('csvParsingErrorsModal.row') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="error in sortErrorsByRowNumber(
            deduplicateErrors(csvParsingErrors),
          )"
          :key="error.index"
        >
          <template
            v-if="['TooFewFields', 'TooManyFields'].includes(error.code)"
          >
            <td>
              {{
                $t(
                  `csvParsingErrorsModal.${error.code}`,
                  getExpectedAndReceivedFieldsFromErrorMessage(error.message),
                )
              }}
            </td>
          </template>
          <template v-else>
            <td>
              {{ $t(`csvParsingErrorsModal.${error.code}`) }}
            </td>
          </template>
          <td v-if="error.row !== undefined">
            <!-- The returned row number is zero-based so 1 has to be added -->
            {{ error.row + 1 }}
          </td>
        </tr>
      </tbody>
    </table>
  </modal-wrapper>
</template>

<script lang="ts" setup>
  import { ParseError } from 'papaparse';
  import { useModalWrapper } from '../ModalWrapper';
  import { ModalSize } from '../ModalWrapper/ModalWrapper.types';

  const { csvParsingErrors = [] } = defineProps<{
    csvParsingErrors?: ParseError[];
  }>();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  function getExpectedAndReceivedFieldsFromErrorMessage(errorMessage: string) {
    const EXPECTED_FIELDS_REGEX = /expected (\d+) fields/i;
    const PARSED_FIELDS_REGEX = /parsed (\d+)$/i;

    const expectedFieldsNumber = Number(
      errorMessage.match(EXPECTED_FIELDS_REGEX)?.[1],
    );
    const parsedFieldsNumber = Number(
      errorMessage.match(PARSED_FIELDS_REGEX)?.[1],
    );

    return {
      expectedFieldsNumber,
      parsedFieldsNumber,
    };
  }

  function deduplicateErrors(errors: ParseError[]): ParseError[] {
    const stringifiedErrors = errors.map((error) => JSON.stringify(error));
    const deduplicatedStringifiedErrors = Array.from(
      new Set(stringifiedErrors),
    );
    return deduplicatedStringifiedErrors.map((error) => JSON.parse(error));
  }

  function sortErrorsByRowNumber(errors: ParseError[]): ParseError[] {
    return errors.sort((a, b) => a.row! - b.row!);
  }
</script>

<style lang="scss" scoped>
  .csv-parsing-modal {
    table {
      width: 90%;
      margin: 0 5%;

      th {
        line-height: 3rem;
      }
    }
  }
</style>
